// ─────────────────────────────────────────────────────────────────────────────
// Students' character fish, drawn from their choices in the fish builder.
// Drawn facing right; mirrored (a snap, never a squash) to face left. Because
// it's drawn from parts, the tail wags faster when swimming, the side fin
// flaps and the eyes blink — things the still art can't do.
// ─────────────────────────────────────────────────────────────────────────────

import { cleanGear, drawFace, drawHat, drawTrail, type Gear } from './gear';

export type Look = {
  body: 'round' | 'long' | 'tall' | 'puffer';
  tail: 'fan' | 'fork' | 'round' | 'lyre';
  fin: 'small' | 'spiky' | 'sail' | 'none';
  eyes: 'big' | 'small' | 'cute' | 'sleepy';
  mouth: 'smile' | 'o' | 'grin' | 'pout';
  pattern: 'none' | 'stripes' | 'spots' | 'belly';
  extra: 'none' | 'crown' | 'bow' | 'glasses' | 'lure';
  bodyColor: string;
  finColor: string;
  patternColor: string;
  /** Bought in the shop (game/gear.ts); the database drops items a student doesn't own. */
  gear?: Gear;
};

export const COLORS = [
  '#ff8a3d', '#ffc53d', '#ffe8a3', '#f25c7a', '#ff9ecb', '#b07cff',
  '#6f7dff', '#3db8ff', '#3ddbc4', '#6ad46a', '#8a5a3b', '#f4f4f4', '#5b6b7f', '#2c2f3a',
];

export const OPTIONS: { [K in Exclude<keyof Look, 'bodyColor' | 'finColor' | 'patternColor' | 'gear'>]: { value: Look[K]; label: string }[] } = {
  body: [{ value: 'round', label: 'Round' }, { value: 'long', label: 'Long' }, { value: 'tall', label: 'Tall' }, { value: 'puffer', label: 'Puffer' }],
  tail: [{ value: 'fan', label: 'Fan' }, { value: 'fork', label: 'Fork' }, { value: 'round', label: 'Paddle' }, { value: 'lyre', label: 'Fancy' }],
  fin: [{ value: 'small', label: 'Small' }, { value: 'spiky', label: 'Spiky' }, { value: 'sail', label: 'Sail' }, { value: 'none', label: 'None' }],
  eyes: [{ value: 'big', label: 'Big' }, { value: 'small', label: 'Small' }, { value: 'cute', label: 'Sparkly' }, { value: 'sleepy', label: 'Sleepy' }],
  mouth: [{ value: 'smile', label: 'Smile' }, { value: 'o', label: 'Oh!' }, { value: 'grin', label: 'Grin' }, { value: 'pout', label: 'Pout' }],
  pattern: [{ value: 'none', label: 'Plain' }, { value: 'stripes', label: 'Stripes' }, { value: 'spots', label: 'Spots' }, { value: 'belly', label: 'Belly' }],
  extra: [{ value: 'none', label: 'None' }, { value: 'crown', label: 'Crown' }, { value: 'bow', label: 'Bow' }, { value: 'glasses', label: 'Glasses' }, { value: 'lure', label: 'Glow lure' }],
};

export const DEFAULT_LOOK: Look = {
  body: 'round', tail: 'fan', fin: 'small', eyes: 'big', mouth: 'smile', pattern: 'stripes', extra: 'none',
  bodyColor: '#ff8a3d', finColor: '#ffc53d', patternColor: '#f4f4f4',
};

const pickOne = <T,>(a: readonly T[]) => a[Math.floor(Math.random() * a.length)];
export function randomLook(): Look {
  const o = OPTIONS;
  const [b, f, p] = [pickOne(COLORS), pickOne(COLORS), pickOne(COLORS)];
  return {
    body: pickOne(o.body).value, tail: pickOne(o.tail).value, fin: pickOne(o.fin).value, eyes: pickOne(o.eyes).value,
    mouth: pickOne(o.mouth).value, pattern: pickOne(o.pattern).value, extra: Math.random() < 0.6 ? 'none' : pickOne(o.extra).value,
    bodyColor: b, finColor: f === b ? pickOne(COLORS) : f, patternColor: p === b ? '#f4f4f4' : p,
  };
}

/** Anything stored that isn't a valid choice falls back to the default. */
export function cleanLook(x: unknown): Look {
  const v = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const opt = <K extends keyof typeof OPTIONS>(k: K) =>
    (OPTIONS[k].some(o => o.value === v[k]) ? v[k] : DEFAULT_LOOK[k]) as Look[K];
  const col = (k: 'bodyColor' | 'finColor' | 'patternColor') =>
    (typeof v[k] === 'string' && /^#[0-9a-f]{6}$/i.test(v[k] as string) ? v[k] : DEFAULT_LOOK[k]) as string;
  return {
    body: opt('body'), tail: opt('tail'), fin: opt('fin'), eyes: opt('eyes'), mouth: opt('mouth'), pattern: opt('pattern'),
    extra: opt('extra'), bodyColor: col('bodyColor'), finColor: col('finColor'), patternColor: col('patternColor'),
    ...(cleanGear(v.gear) ? { gear: cleanGear(v.gear) } : {}),
  };
}

const INK = '#1e2633';
const BODY: Record<Look['body'], { rx: number; ry: number }> = {
  round: { rx: 1, ry: 0.82 }, long: { rx: 1.3, ry: 0.56 }, tall: { rx: 0.86, ry: 1.02 }, puffer: { rx: 0.96, ry: 0.94 },
};

function shade(hex: string, k: number): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(k > 0 ? c + (255 - c) * k : c * (1 + k))));
  return `rgb(${f(n >> 16)},${f((n >> 8) & 255)},${f(n & 255)})`;
}

export type AvatarDraw = {
  x: number; y: number;
  /** Body length in canvas px (roughly). */
  size: number;
  facing?: 1 | -1;
  /** 0 idle … 1 full speed: how fast the tail beats. */
  effort?: number;
  /** Nose up/down, radians. */
  tilt?: number;
  /** Time in seconds. */
  t: number;
  /** Seconds offset so a crowd doesn't blink together. */
  seed?: number;
};

export function drawAvatar(g: CanvasRenderingContext2D, look: Look, o: AvatarDraw) {
  const u = o.size / 2.6;
  const { rx, ry } = BODY[look.body];
  const t = o.t + (o.seed ?? 0);
  const effort = Math.min(1, Math.max(0, o.effort ?? 0));
  const wag = Math.sin(t * (4 + effort * 10)) * (0.18 + effort * 0.22);
  const lw = Math.max(1.2, u * 0.08);

  g.save();
  g.translate(o.x, o.y);
  if (o.facing === -1) g.scale(-1, 1);
  if (o.tilt) g.rotate(o.tilt);
  g.scale(u, u);
  g.lineWidth = lw / u;
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.strokeStyle = INK;

  if (look.gear?.trail) drawTrail(g, look.gear.trail, rx, t);

  // Tail, swinging from where it joins the body.
  g.save();
  g.translate(-rx * 0.86, 0);
  g.rotate(wag);
  g.beginPath();
  if (look.tail === 'fan') {
    g.moveTo(0.1, 0);
    g.quadraticCurveTo(-0.4, -0.25, -0.8, -0.62);
    g.quadraticCurveTo(-0.62, 0, -0.8, 0.62);
    g.quadraticCurveTo(-0.4, 0.25, 0.1, 0);
  } else if (look.tail === 'fork') {
    g.moveTo(0.1, -0.08);
    g.lineTo(-0.75, -0.62); g.quadraticCurveTo(-0.55, -0.2, -0.42, 0);
    g.quadraticCurveTo(-0.55, 0.2, -0.75, 0.62); g.lineTo(0.1, 0.08);
  } else if (look.tail === 'round') {
    g.moveTo(0.1, -0.12);
    g.bezierCurveTo(-0.5, -0.55, -0.95, -0.3, -0.85, 0);
    g.bezierCurveTo(-0.95, 0.3, -0.5, 0.55, 0.1, 0.12);
  } else {
    g.moveTo(0.1, -0.06);
    g.bezierCurveTo(-0.5, -0.2, -0.9, -0.9, -1.15, -0.75);
    g.quadraticCurveTo(-0.7, -0.2, -0.55, 0);
    g.quadraticCurveTo(-0.7, 0.2, -1.15, 0.75);
    g.bezierCurveTo(-0.9, 0.9, -0.5, 0.2, 0.1, 0.06);
  }
  g.closePath();
  g.fillStyle = look.finColor; g.fill(); g.stroke();
  g.restore();

  // Top fin.
  if (look.fin !== 'none') {
    g.beginPath();
    const top = -ry * 0.92;
    if (look.fin === 'small') {
      g.moveTo(-0.35, top + 0.08); g.quadraticCurveTo(-0.1, top - 0.38, 0.25, top + 0.06);
    } else if (look.fin === 'spiky') {
      g.moveTo(-0.55, top + 0.12);
      for (let i = 0; i < 4; i++) { const x = -0.45 + i * 0.22; g.lineTo(x, top - 0.36 - (i % 2) * 0.08); g.lineTo(x + 0.11, top + 0.02); }
      g.lineTo(0.4, top + 0.12);
    } else {
      g.moveTo(-0.6, top + 0.12); g.bezierCurveTo(-0.5, top - 0.8, 0.1, top - 0.75, 0.35, top + 0.1);
    }
    g.closePath();
    g.fillStyle = look.finColor; g.fill(); g.stroke();
  }

  // Body.
  const body = new Path2D();
  body.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
  const grad = g.createLinearGradient(0, -ry, 0, ry);
  grad.addColorStop(0, shade(look.bodyColor, 0.25));
  grad.addColorStop(0.55, look.bodyColor);
  grad.addColorStop(1, shade(look.bodyColor, -0.18));
  g.fillStyle = grad;
  g.fill(body);

  // Pattern, kept inside the body.
  if (look.pattern !== 'none') {
    g.save();
    g.clip(body);
    g.fillStyle = look.patternColor;
    if (look.pattern === 'stripes') {
      for (const x of [-0.45, 0.05]) { g.beginPath(); g.ellipse(x, 0, 0.13, ry * 1.1, 0.12, 0, Math.PI * 2); g.fill(); }
    } else if (look.pattern === 'spots') {
      for (const [x, y, r] of [[-0.55, -0.2, 0.12], [-0.2, 0.25, 0.1], [0.05, -0.38, 0.09], [-0.35, -0.5, 0.07], [0.2, 0.3, 0.07], [-0.7, 0.2, 0.08]]) {
        g.beginPath(); g.arc(x, y * ry, r, 0, Math.PI * 2); g.fill();
      }
    } else {
      g.beginPath(); g.ellipse(0.1, ry * 0.75, rx * 0.95, ry * 0.55, 0, 0, Math.PI * 2); g.fill();
    }
    g.restore();
  }
  g.stroke(body);

  // Puffer spikes.
  if (look.body === 'puffer') {
    g.fillStyle = look.finColor;
    for (let i = 0; i < 9; i++) {
      const a = Math.PI * (0.65 + i * 0.17);
      const bx = Math.cos(a) * rx, by = Math.sin(a) * ry;
      g.beginPath();
      g.moveTo(bx + Math.cos(a + 0.18) * 0.02, by + Math.sin(a + 0.18) * 0.02);
      g.lineTo(bx * 1.2, by * 1.2);
      g.lineTo(bx + Math.cos(a - 0.18) * 0.02, by + Math.sin(a - 0.18) * 0.02);
      g.fill(); g.stroke();
    }
  }

  // Side fin, flapping.
  g.save();
  g.translate(-0.05, ry * 0.25);
  g.rotate(0.5 + Math.sin(t * (5 + effort * 6)) * 0.35);
  g.beginPath(); g.ellipse(-0.18, 0, 0.26, 0.11, 0, 0, Math.PI * 2);
  g.fillStyle = look.finColor; g.fill(); g.stroke();
  g.restore();

  // Eye, with a blink every few seconds.
  const ex = rx * 0.5, ey = -ry * 0.22;
  const er = look.eyes === 'big' ? 0.27 : look.eyes === 'small' ? 0.15 : look.eyes === 'cute' ? 0.24 : 0.22;
  const blink = (t % 4.2) < 0.13;
  if (blink) {
    g.beginPath(); g.moveTo(ex - er, ey); g.quadraticCurveTo(ex, ey + er * 0.5, ex + er, ey); g.stroke();
  } else {
    g.beginPath(); g.arc(ex, ey, er, 0, Math.PI * 2);
    g.fillStyle = '#fff'; g.fill(); g.stroke();
    const pr = look.eyes === 'cute' ? er * 0.7 : er * 0.5;
    g.beginPath(); g.arc(ex + er * 0.22, ey + er * 0.05, pr, 0, Math.PI * 2);
    g.fillStyle = INK; g.fill();
    g.beginPath(); g.arc(ex + er * 0.05, ey - er * 0.2, pr * 0.35, 0, Math.PI * 2);
    g.fillStyle = '#fff'; g.fill();
    if (look.eyes === 'cute') { g.beginPath(); g.arc(ex + er * 0.45, ey + er * 0.3, pr * 0.18, 0, Math.PI * 2); g.fill(); }
    if (look.eyes === 'sleepy') {
      g.beginPath(); g.arc(ex, ey, er, Math.PI, Math.PI * 2); g.closePath();
      g.fillStyle = shade(look.bodyColor, -0.1); g.fill(); g.stroke();
    }
  }

  // Mouth.
  const mx = rx * 0.9, my = ry * 0.18;
  g.beginPath();
  if (look.mouth === 'smile') { g.arc(mx - 0.14, my - 0.06, 0.13, 0.2, 1.4); g.stroke(); }
  else if (look.mouth === 'o') { g.arc(mx - 0.05, my, 0.07, 0, Math.PI * 2); g.fillStyle = '#5a1f2b'; g.fill(); g.stroke(); }
  else if (look.mouth === 'grin') {
    g.moveTo(mx - 0.32, my - 0.02); g.quadraticCurveTo(mx - 0.15, my + 0.2, mx + 0.02, my - 0.04);
    g.closePath(); g.fillStyle = '#fff'; g.fill(); g.stroke();
  } else { g.ellipse(mx - 0.02, my, 0.07, 0.1, 0, 0, Math.PI * 2); g.fillStyle = '#f25c7a'; g.fill(); g.stroke(); }

  // Extras.
  const hat = !!look.gear?.hat, face = !!look.gear?.face;
  if (look.extra === 'crown' && !hat) {
    const cx = rx * 0.15, cy = -ry - 0.02;
    g.beginPath();
    g.moveTo(cx - 0.3, cy + 0.05); g.lineTo(cx - 0.32, cy - 0.3); g.lineTo(cx - 0.15, cy - 0.12); g.lineTo(cx, cy - 0.36);
    g.lineTo(cx + 0.15, cy - 0.12); g.lineTo(cx + 0.32, cy - 0.3); g.lineTo(cx + 0.3, cy + 0.05); g.closePath();
    g.fillStyle = '#ffd23d'; g.fill(); g.stroke();
  } else if (look.extra === 'bow' && !hat) {
    const cx = -0.1, cy = -ry - 0.05;
    g.fillStyle = '#ff5c8a';
    for (const s of [-1, 1]) { g.beginPath(); g.moveTo(cx, cy); g.lineTo(cx + s * 0.3, cy - 0.18); g.lineTo(cx + s * 0.3, cy + 0.14); g.closePath(); g.fill(); g.stroke(); }
    g.beginPath(); g.arc(cx, cy, 0.07, 0, Math.PI * 2); g.fill(); g.stroke();
  } else if (look.extra === 'glasses' && !blink && !face) {
    g.beginPath(); g.arc(ex, ey, er + 0.07, 0, Math.PI * 2); g.stroke();
    g.beginPath(); g.moveTo(ex - er - 0.07, ey); g.lineTo(ex - er - 0.3, ey - 0.05); g.stroke();
  } else if (look.extra === 'lure') {
    const bob = Math.sin(t * 2) * 0.05;
    g.beginPath(); g.moveTo(rx * 0.2, -ry * 0.95); g.quadraticCurveTo(rx * 0.7, -ry - 0.6, rx * 1.15, -ry * 0.55 + bob); g.stroke();
    g.beginPath(); g.arc(rx * 1.15, -ry * 0.5 + bob, 0.11, 0, Math.PI * 2);
    g.fillStyle = '#fff6a8'; g.shadowColor = '#fff6a8'; g.shadowBlur = 12; g.fill(); g.shadowBlur = 0; g.stroke();
  }

  // Shop gear, over everything (a bought hat sits where the crown or bow would).
  if (look.gear?.face && !blink) drawFace(g, look.gear.face, ex, ey, er, mx, my);
  else if (look.gear?.face === 'mustache') drawFace(g, 'mustache', ex, ey, er, mx, my);
  if (look.gear?.hat) drawHat(g, look.gear.hat, rx * 0.15, -ry * 0.98, t);
  g.restore();
}

/** Where a character's light sits (the lure glows brighter), relative to its centre. */
export const hasLure = (l: Look) => l.extra === 'lure';
